import { SiteLayout, PageHero, LightSection, CtaStrip } from "@/components/site/SiteLayout";

const PRINCIPLES = [
  ["01", "Readiness is measurable — and must be explainable", "Every engagement produces a transparent readiness verdict per market and globally. Every weight, threshold and assumption is on the table. If a number can't be defended in front of your board, we don't present it."],
  ["02", "Markets are grouped by payer behaviour, not geography", "Two markets on different continents can evaluate evidence identically; two neighbours can differ completely. We plan by how payers decide — which is why evidence built for one market so often unlocks several."],
  ["03", "Evidence is designed for reuse", "The most expensive evidence strategy is the one rebuilt for every market. We identify what travels as-is, what needs local adaptation, and what is irreducibly local — before budgets are committed."],
  ["04", "Precedent is strategy", "Someone was usually assessed before you. Incumbent approvals, restrictions and pricing anchors tell you the questions that will be asked — and the answers that worked."],
  ["05", "AI does the scale. Consultants do the judgment.", "AI reads the documents, maps the requirements and keeps pace with every market simultaneously. Senior consultants validate every finding, interpret every score and stand behind every recommendation. No raw AI output ever reaches you."],
];

const DIMENSIONS = [
  ["Global Readiness", "One governed verdict on whether — and where — you are ready to submit."],
  ["Evidence Reusability", "Where each asset is accepted, needs adaptation, or stops working — with the reason."],
  ["Gap Identification", "The exact missing evidence per market, severity-ranked with concrete actions."],
  ["Submission Sequencing", "Waves ordered by readiness, precedent leverage and reference-pricing dependencies."],
  ["Value Dossier Strength", "How well your GVD carries every target market — and the enhancement plan when it doesn't."],
  ["Competitive Position", "Who was reimbursed before you, where, and the precedent you can use."],
];

export default function OurApproach() {
  return (
    <SiteLayout>
      <PageHero overline="Our Approach" title="AI-enabled. Expert-led. Accountable."
        lede="MAI Matrix™ is not software you license — it is how our consultants work. Five principles shape every engagement." />
      <LightSection>
        <div className="space-y-px bg-slate-200 border border-slate-200">
          {PRINCIPLES.map(([n, title, text]) => (
            <div key={n} className="bg-white p-7 grid grid-cols-1 md:grid-cols-12 gap-4">
              <p className="font-mono-data text-emerald-700 font-bold text-sm md:col-span-1">{n}</p>
              <p className="font-heading font-bold text-slate-900 text-base md:col-span-4">{title}</p>
              <p className="text-sm text-slate-500 leading-relaxed md:col-span-7">{text}</p>
            </div>
          ))}
        </div>
      </LightSection>
      <LightSection alt overline="Strategic dimensions" title="Every engagement builds readiness across six dimensions.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {DIMENSIONS.map(([title, text]) => (
            <div key={title} className="bg-white border border-slate-200 p-6">
              <p className="font-heading font-bold text-slate-900 text-sm">{title}</p>
              <p className="text-sm text-slate-500 leading-relaxed mt-2">{text}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-6 max-w-2xl">How these are computed is our proprietary methodology — what they mean, and why, is explained in full in every deliverable.</p>
      </LightSection>
      <CtaStrip text="See the approach applied to your own product — on a live walkthrough." />
    </SiteLayout>
  );
}
